// Shared player avatar — take-two `<Avatar>` (WS4, ARCA-84).
//
// Ports the prototype ui.jsx Avatar onto the part-1 `.avatar` recipe (pieces.css:
// 48 square · r15 · Fredoka-20 white on a warm relief). The CSS holds the size-48
// default; this component scales geometry with `size` and paints the per-player
// gradient + optional ring inline, exactly like the prototype:
//   background: linear-gradient(160deg, color, color-mix(color 70% ink))
//   ring     : keep the bottom relief, add a 3px outer ring
//
// Decorative by default (aria-hidden) — it sits beside the player's name, which
// carries the accessible identity. Replaces the per-screen inline avatar spans
// duplicated across Friends / Leaderboard / Profile.

import type { CSSProperties, JSX } from 'react'

export interface AvatarProps {
  /** Single-glyph initial rendered inside the tile (already upper-cased). */
  readonly initial: string
  /**
   * Base CSS color for the gradient. Default ember (House-Ember / player-a),
   * matching the app's single-house avatar treatment.
   */
  readonly color?: string
  /** Square edge in px. Default 48 — the `.avatar` recipe size. */
  readonly size?: number
  /** When set, draws a coloured status ring (e.g. online / your-turn). */
  readonly ring?: string
  /** Forwarded test id (callers preserve their existing avatar selectors). */
  readonly dataTestid?: string
}

/**
 * Folk-art player avatar. Geometry scales from `size`; colour is painted inline
 * over the shared `.avatar` recipe so a single CSS rule owns the typography and
 * warm relief. Mirrors the take-two prototype Avatar (ui.jsx).
 */
export function Avatar({
  initial,
  color = 'var(--color-player-a)',
  size = 48,
  ring,
  dataTestid,
}: AvatarProps): JSX.Element {
  const style: CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.42),
    borderRadius: Math.round(size * 0.31),
    background: `linear-gradient(160deg, ${color}, color-mix(in srgb, ${color} 70%, var(--color-ink)))`,
  }
  if (ring !== undefined) {
    // Replace the recipe relief with the bottom-inset + outer ring (prototype).
    style.boxShadow = `inset 0 -3px 0 color-mix(in srgb, var(--color-ink) 12%, transparent), 0 0 0 3px ${ring}`
  }
  return (
    <span
      className="avatar"
      aria-hidden="true"
      data-testid={dataTestid}
      style={style}
    >
      {initial}
    </span>
  )
}
